import type { Prescription } from '~/api/graphql/generated/types'
import { usePrescriptionsWidgetSubscription } from '~/api/graphql/subscriptions/prescriptions'
import { convertPrescriptionV2ToPrescription } from '~/utils/prescription'

import { useSubscriptionRetry } from './useSubscriptionRetry'

/**
 * Custom hook for subscribing to prescription data changes for a specific patient
 * Automatically handles subscription retries for network errors
 *
 * @param patientId - The ID of the patient to subscribe to prescription updates for
 * @param offset - Pagination offset for prescriptions list
 * @param limit - Maximum number of prescriptions to fetch per page
 * @returns Object containing prescriptions array, loading state, and error state
 */
export const usePrescriptionsSubscription = (
  patientId: string,
  offset: number,
  limit: number,
) => {
  const { data, loading, error, restart } = usePrescriptionsWidgetSubscription(
    patientId,
    offset,
    limit,
  )

  const graphqlPrescriptions: Prescription[] =
    data?.prescriptionWidget?.prescriptions || []

  const totalCount = data?.prescriptionWidget?.prescriptionCounters
    ?.prescriptionCount as number

  const transformedPrescriptions = graphqlPrescriptions
    .filter((entry) => entry.__typename === 'Prescription')
    .map((prescription) => convertPrescriptionV2ToPrescription(prescription))
    .filter((prescription) => prescription !== undefined)

  useSubscriptionRetry({
    error,
    restart,
    subscriptionName: 'Prescriptions subscription',
  })

  return {
    prescriptions: transformedPrescriptions,
    totalCount,
    loading,
    error,
  }
}
